// ═══════════════════════════════════════════════════════════════
// Tabla de validación: muestra filas válidas, con warnings y con errores
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronUp, Search } from 'lucide-react';

const FILTROS = [
  { key: 'todos', label: 'Todos' },
  { key: 'valido', label: 'Válidos' },
  { key: 'warning', label: 'Advertencias' },
  { key: 'error', label: 'Errores' },
];

const estadoConfig = {
  valido: {
    icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
    badge: 'bg-green-100 text-green-700',
    label: 'Válido',
  },
  warning: {
    icon: <AlertTriangle className="w-4 h-4 text-yellow-500" />,
    badge: 'bg-yellow-100 text-yellow-700',
    label: 'Advertencia',
  },
  error: {
    icon: <XCircle className="w-4 h-4 text-red-500" />,
    badge: 'bg-red-100 text-red-700',
    label: 'Error',
  },
};

const FilaDetalle = ({ fila }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = estadoConfig[fila.estado] ?? estadoConfig.error;
  const tieneDetalle = fila.errores?.length > 0 || fila.warnings?.length > 0;

  return (
    <div className={`border rounded-lg overflow-hidden ${
      fila.estado === 'error'
        ? 'border-red-200 bg-red-50'
        : fila.estado === 'warning'
          ? 'border-yellow-200 bg-yellow-50'
          : 'border-green-200 bg-green-50'
    }`}>
      <div
        className={`flex items-center gap-3 px-4 py-2.5 ${tieneDetalle ? 'cursor-pointer' : ''}`}
        onClick={() => tieneDetalle && setExpanded(e => !e)}
      >
        {/* Número de fila */}
        <span className="text-xs text-gray-400 w-10 text-right flex-shrink-0">
          #{fila.fila}
        </span>

        {/* Ícono estado */}
        <span className="flex-shrink-0">{cfg.icon}</span>

        {/* Nombre completo */}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 truncate">
            {[fila.nombre, fila.apellido].filter(Boolean).join(' ') || <em className="text-gray-400">Sin nombre</em>}
          </span>
          {fila.cedula && (
            <span className="ml-2 text-xs text-gray-500">{fila.cedula}</span>
          )}
        </div>

        {/* Badge estado */}
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${cfg.badge}`}>
          {cfg.label}
        </span>

        {/* Expandir */}
        {tieneDetalle && (
          expanded
            ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
            : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </div>

      {/* Detalle errores/warnings */}
      {expanded && tieneDetalle && (
        <div className="px-4 pb-3 space-y-1.5 border-t border-gray-200 pt-2">
          {fila.errores?.map((msg, i) => (
            <div key={`e${i}`} className="flex items-start gap-2 text-xs text-red-700">
              <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{msg}</span>
            </div>
          ))}
          {fila.warnings?.map((msg, i) => (
            <div key={`w${i}`} className="flex items-start gap-2 text-xs text-yellow-700">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ValidationTable = ({ filas = [] }) => {
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  const counts = useMemo(() => ({
    todos: filas.length,
    valido: filas.filter(f => f.estado === 'valido').length,
    warning: filas.filter(f => f.estado === 'warning').length,
    error: filas.filter(f => f.estado === 'error').length,
  }), [filas]);

  const filasFiltradas = useMemo(() => {
    let result = filtro === 'todos' ? filas : filas.filter(f => f.estado === filtro);
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      result = result.filter(f =>
        f.nombre?.toLowerCase().includes(q) ||
        f.apellido?.toLowerCase().includes(q) ||
        f.cedula?.includes(q)
      );
    }
    return result;
  }, [filas, filtro, busqueda]);

  return (
    <div className="space-y-3">
      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTROS.map(f => (
          <button
            key={f.key}
            onClick={() => setFiltro(f.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              filtro === f.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f.label}
            <span className={`rounded-full px-1.5 py-0.5 text-xs ${
              filtro === f.key ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              {counts[f.key]}
            </span>
          </button>
        ))}

        {/* Buscador */}
        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o cédula..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg w-52 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Lista de filas */}
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {filasFiltradas.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-8">
            No hay filas que coincidan con el filtro.
          </div>
        ) : (
          filasFiltradas.map((fila, idx) => (
            <FilaDetalle key={fila.fila ?? idx} fila={fila} />
          ))
        )}
      </div>

      {filasFiltradas.length > 0 && (
        <p className="text-xs text-gray-400 text-right">
          Mostrando {filasFiltradas.length} de {filas.length} filas
        </p>
      )}
    </div>
  );
};

export default ValidationTable;
